import type { PhoneNumber } from "./phoneNumberService"

/** Canonical + optional Retell/Twilio-style pretty (SMS, voice, or API rows). */
export interface PhoneDisplayFields {
  phone_number: string
  phone_number_pretty?: string | null
}

function digitsOnly(s: string): string {
  return String(s ?? "").replace(/\D/g, "")
}

/** US national 10 digits, or 11 with leading country code 1. */
function usNational10(raw: string): string | null {
  const d = digitsOnly(raw)
  if (d.length === 10) return d
  if (d.length === 11 && d.startsWith("1")) return d.slice(1)
  return null
}

/** Format a string that contains a US NANP number to (XXX) XXX-XXXX. */
export function formatPhonePretty(raw: string): string {
  const national = usNational10(raw)
  if (national)
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
  return String(raw ?? "").trim()
}

/**
 * Pretty display for any row with `phone_number` and optional `phone_number_pretty`.
 * Prefer canonical `phone_number`, then `phone_number_pretty`.
 */
export function getDisplayPhonePrettyFromFields(p: PhoneDisplayFields): string {
  const canonical = String(p.phone_number ?? "")
  if (usNational10(canonical)) return formatPhonePretty(canonical)
  const pretty = String(p.phone_number_pretty ?? "")
  if (usNational10(pretty)) return formatPhonePretty(pretty)
  return (pretty || canonical).trim()
}

export function getDisplayPhonePretty(p: PhoneNumber): string {
  return getDisplayPhonePrettyFromFields(p)
}

/** True if the label is almost certainly a phone value (not e.g. an agent id with many digits). */
export function isLikelyUsPhoneLabel(s: string): boolean {
  return usNational10(s) !== null
}

/**
 * Card title: real nicknames stay as-is; values that look like phone numbers get formatted
 * so we never show raw +1XXXXXXXXXX next to a formatted line.
 */
export function getVoicePhoneCardTitle(p: PhoneNumber): string {
  const nick = p.nickname?.trim()
  if (!nick) return getDisplayPhonePretty(p)
  if (isLikelyUsPhoneLabel(nick)) return formatPhonePretty(nick)
  return nick
}

/** Second line under title: show formatted number only when the title is a human nickname. */
export function shouldShowVoicePhoneCardSubtitle(p: PhoneNumber): boolean {
  const nick = p.nickname?.trim()
  return !!nick && !isLikelyUsPhoneLabel(nick)
}
